import { Component, Host, h, Prop, Element, State, Watch } from "@stencil/core";

import { ChCheckboxCustomEvent } from "@genexus/chameleon-controls-library";
import {
  TreeViewItemModelExtended,
  TreeViewModel
} from "@genexus/chameleon-controls-library";
import { MercuryBundles } from "@genexus/mercury";

import { Locale } from "../../common/locale";
import {
  updateObjectsTreeModel,
  convertKbPropertiesDataToTreeItemData,
  clearKbPropertiesFromTree
} from "./helpers";
import { ObjectType } from "../../common/types";

import { KB_PROPERTY, KB_OBJECT } from "./helpers";
import {
  KBPropertiesCallback,
  KBPropertyData,
  KBPropertyType,
  ObjectData,
  ExportFileDirectoryCallback,
  OptionsCallback,
  AddObjectsCallback,
  ExportCallback,
  AddReferencesCallback,
  CancelCallback
} from "./types";

import { getIconPath } from "@genexus/mercury";

const FILE_ICON = getIconPath({
  category: "gemini-tools",
  name: "file",
  colorType: "on-surface"
});
const SETTINGS_ICON = getIconPath({
  category: "gemini-tools",
  name: "settings",
  colorType: "primary"
});
const GENERAL_REFERENCES_ICON = getIconPath({
  category: "window-tools",
  name: "references",
  colorType: "interactive"
});
const MENUS_NEW_OBJECT_ICON = getIconPath({
  category: "menus",
  name: "new-object",
  colorType: "on-primary"
});
const TREE_VIEW_ICON = getIconPath({
  category: "controls",
  name: "treeview"
});
const CHECKBOX_CHECKED_VALUE = "true";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/checkbox",
  "components/edit",
  "components/icon",
  "components/tree-view",
  "utils/form",
  "utils/layout",
  "utils/spacing",
  "utils/typography"
];
@Component({
  tag: "gx-ide-kb-manager-export",
  styleUrl: "kb-manager-export.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/kb-manager-export"]
})
export class GxIdeKbManagerExport {
  #addKbDescriptionEl!: HTMLChCheckboxElement;
  #componentLocale: any;
  #animateEmptyState: boolean = false;
  #exportAllEl!: HTMLChCheckboxElement;
  #fileNameEl!: HTMLChEditElement;
  #kBPropertiesData!: KBPropertyData[];
  /**
   * Stores the object types information (name, and icon)
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #objectTypesMap: Map<string, ObjectType> = new Map();
  /**
   * Stores the object's id that have been added.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #objectsSet: Set<string> = new Set();

  @Element() el: HTMLGxIdeKbManagerExportElement;

  @State() addKbPropertiesIsChecked = false;
  @State() atLeastOnePropertyItemIsChecked = false;
  @State() atLeastOneObjectItemIsChecked = false;
  @State() checkedObjectsIds: string[] = [];
  @State() checkedPropertiesIds: string[] = [];
  @State() exportAllIsChecked = false;
  @State() exportingIsInProcess = false;
  @State() hasObjects: boolean;
  @State() objectsTreeViewModel: TreeViewModel = [];
  @Watch("objectsTreeViewModel")
  objectsTreeViewModelChanged(newObjectsTreeViewModel: TreeViewModel) {
    this.hasObjects =
      newObjectsTreeViewModel && newObjectsTreeViewModel.length > 0;
    if (!this.hasObjects) {
      this.atLeastOnePropertyItemIsChecked = false;
      this.atLeastOneObjectItemIsChecked = false;
    }
  }

  /**
   * Callback invoked when the user wants to include KB properties.
   */
  @Prop() readonly addKBPropertiesCallback!: KBPropertiesCallback;

  /**
   * Callback invoked when the user wants to add objects.
   */
  @Prop() readonly addObjectsCallback!: AddObjectsCallback;

  /**
   *Callback invoked when the user wants to add all the references for the selected objects.
   *@param itemIds:string[] The selected item ids
   *@returns : Returns an object with the items and its references
   */
  @Prop() readonly addReferencesCallback!: AddReferencesCallback;

  /**
   * Callback invoked when the user wants to cancel the export process.
   * @returns It returns a boolean indicating whether the process could be canceled or not.
   */
  @Prop() readonly cancelCallback!: CancelCallback;

  /**
   *Callback invoked when the user wants to initiate the export process.
   *@param fileName:string
   *@param itemIds:string[] The selected item ids
   *@returns : It returns a boolean indicating whether the process was successful
   */
  @Prop() readonly exportCallback!: ExportCallback;

  /**
   * Callback invoked when the user wants to access the export options.
   */
  @Prop() readonly exportFileDirectoryCallback!: ExportFileDirectoryCallback;

  /**
   * It allows defining the default Export File Name
   */
  @Prop() readonly fileName: string;

  /**
   * Array with the possible types of KB properties
   */
  @Prop() readonly kbPropertiesTypes: KBPropertyType[];

  /**
   * If true, it will display a loader when needed.
   */
  @Prop() readonly loader = false;

  /**
   * Array of possible object types
   */
  @Prop() readonly objectTypes: ObjectType[];
  @Watch("objectTypes")
  objectTypesChanged(newObjectTypesArray: ObjectType[]) {
    this.#objectsTypesArrayToMap(newObjectTypesArray);
  }

  /**
   * Callback invoked when the user wants to access the export options.
   */
  @Prop() readonly optionsCallback: OptionsCallback;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#evaluateObjects();
    this.#objectsTypesArrayToMap(this.objectTypes);
  }

  #addKBPropertiesCallbackHandler = async (
    _e: ChCheckboxCustomEvent<any> | InputEvent
  ) => {
    this.addKbPropertiesIsChecked =
      this.#addKbDescriptionEl.value === CHECKBOX_CHECKED_VALUE;
    if (this.addKbPropertiesIsChecked) {
      this.#kBPropertiesData = await this.addKBPropertiesCallback();
      if (this.#kBPropertiesData.length) {
        this.objectsTreeViewModel = convertKbPropertiesDataToTreeItemData(
          this.#kBPropertiesData,
          this.kbPropertiesTypes,
          this.objectsTreeViewModel
        );
      }
    } else {
      // previous properties (if any) should be removed
      this.objectsTreeViewModel = clearKbPropertiesFromTree(
        this.objectsTreeViewModel
      );
    }
  };

  #addObjectsCallbackHandler = async () => {
    this.#exportAllEl.value = undefined;
    this.exportAllIsChecked = false;
    const addedObjects = await this.addObjectsCallback();
    this.#updateObjects(addedObjects);
  };

  #addReferencesCallbackHandler = async () => {
    const objects: ObjectData[] = await this.addReferencesCallback(
      this.checkedObjectsIds
    );
    this.#updateObjects(objects);
  };

  #cancelCallbackHandler = async () => {
    const cancelled = await this.cancelCallback();
    if (cancelled) {
      this.exportingIsInProcess = false;
    }
  };

  #clearButtonHandler = () => {
    this.objectsTreeViewModel = [];
    this.#objectsSet.clear();
    this.#addKbDescriptionEl.value = undefined;
    this.#exportAllEl.value = undefined;
  };

  #evaluateContentToDisplay = () => {
    // Nothing to display on the tree-view
    if (!this.hasObjects && !this.exportAllIsChecked) {
      return (
        <gx-ide-empty-state
          isAnimated={this.#animateEmptyState}
          stateTitle={this.#componentLocale.main.noObjectsToDisplay}
          key="no-objects-empty-state"
        >
          <button
            class="button-primary"
            onClick={this.#addObjectsCallbackHandler}
          >
            {this.#componentLocale.main.beginByADdingSomeObjects}
          </button>
        </gx-ide-empty-state>
      );
    } else {
      this.#animateEmptyState = true; // improve performance if is first render
    }

    if (this.exportAllIsChecked) {
      return (
        <gx-ide-empty-state
          isAnimated={this.#animateEmptyState}
          stateIconSrc={TREE_VIEW_ICON}
          stateTitle={this.#componentLocale.main.exportAllMessage}
          key="exporting-all-empty-state"
        ></gx-ide-empty-state>
      );
    } else {
      this.#animateEmptyState = true; // improve performance if is first render
    }

    return (
      <ch-tree-view-render
        class="tree-view"
        checkbox
        checked
        dragDisabled
        dropDisabled
        filter={this.#evaluateTreeViewFilters()}
        filterType={
          this.addKbPropertiesIsChecked && !this.exportAllIsChecked
            ? "none"
            : "metadata"
        }
        model={this.objectsTreeViewModel}
        showLines="last"
        toggleCheckboxes
        sortItemsCallback={this.#sortTreeItems}
        onCheckedItemsChange={this.#objectsTreeCheckedItemsChangedHandler}
      ></ch-tree-view-render>
    );
  };

  #evaluateObjects = () => {
    this.hasObjects =
      this.objectsTreeViewModel && this.objectsTreeViewModel.length > 0;
  };

  #evaluateTreeViewFilters = (): string => {
    if (this.addKbPropertiesIsChecked && this.exportAllIsChecked) {
      return KB_PROPERTY; // only show properties
    }
    if (!this.addKbPropertiesIsChecked && !this.exportAllIsChecked) {
      return KB_OBJECT; // only show objects
    }
    return "none";
  };

  #exportAllChangedHandler = (
    _event: ChCheckboxCustomEvent<any> | InputEvent
  ) => {
    this.exportAllIsChecked =
      this.#exportAllEl.value === CHECKBOX_CHECKED_VALUE;
  };

  #exportCallbackHandler = async () => {
    this.exportingIsInProcess = true;
    const fileName: string = this.#fileNameEl.value;
    const checkedKbPropertiesIds = this.addKbPropertiesIsChecked
      ? this.checkedPropertiesIds
      : [];
    const checkedObjectIds = this.exportAllIsChecked
      ? undefined
      : this.checkedObjectsIds;
    this.exportCallback(
      fileName,
      checkedKbPropertiesIds,
      checkedObjectIds
    ).then(() => {
      this.exportingIsInProcess = false;
    });
    // returns Promise<boolean> but no procedure was detailed on the spec.
  };

  #objectsTypesArrayToMap(objectTypesArray: ObjectType[]) {
    objectTypesArray.forEach(objectType => {
      this.#objectTypesMap.set(objectType.id, objectType);
    });
  }

  #objectsTreeCheckedItemsChangedHandler = (
    event: CustomEvent<Map<string, TreeViewItemModelExtended>>
  ) => {
    // filter 'checked objects' and 'checked properties' ids
    const checkedObjectsIds: string[] = [];
    const checkedPropertiesIds: string[] = [];
    const allItemsWithCheckbox = [...event.detail.values()];
    allItemsWithCheckbox.forEach(node => {
      if (node.item.checked && node.item.metadata === KB_OBJECT) {
        checkedObjectsIds.push(node.item.id);
      } else if (node.item.checked && node.item.metadata === KB_PROPERTY) {
        checkedPropertiesIds.push(node.item.id);
      }
    });
    this.checkedObjectsIds = checkedObjectsIds;
    this.checkedPropertiesIds = checkedPropertiesIds;

    this.atLeastOnePropertyItemIsChecked = this.checkedPropertiesIds.length > 0;
    this.atLeastOneObjectItemIsChecked = this.checkedObjectsIds.length > 0;
  };

  #sortTreeItems = (subModel: TreeViewModel) => {
    // "property" items should be added on top.
    // else sort alphabetically by "caption" value.
    return subModel.sort((a, b) => {
      if (a.metadata === "property" && b.metadata !== "property") {
        return -1;
      }
      if (b.metadata === "property" && a.metadata !== "property") {
        return 1;
      }

      return a.caption.localeCompare(b.caption);
    });
  };

  #renderFooter = (): HTMLElement => {
    const referencesButtonEnabled = this.atLeastOneObjectItemIsChecked;
    const clearButtonEnabled = this.objectsTreeViewModel.length > 0;
    return (
      <footer class="footer dialog-footer-with-border dialog-footer-space-between space-body spacing-body-inline spacing-body-block-end">
        <button
          class="button-tertiary clear-btn"
          disabled={!clearButtonEnabled}
          part="clear-button"
          onClick={clearButtonEnabled && this.#clearButtonHandler}
        >
          {this.#componentLocale.footer.clearButton}
        </button>

        <div class="buttons-spacer">
          <button
            class="button-secondary button-icon-and-text"
            part="references-button"
            disabled={!referencesButtonEnabled}
            onClick={
              referencesButtonEnabled && this.#addReferencesCallbackHandler
            }
          >
            <ch-image
              class="icon-m"
              disabled={!referencesButtonEnabled}
              src={GENERAL_REFERENCES_ICON}
            ></ch-image>
            {this.#componentLocale.footer.addReferencesButton}
          </button>

          <button
            // Add button
            class="button-primary button-icon-and-text"
            disabled={this.exportAllIsChecked}
            part="add-button"
            onClick={
              !this.exportAllIsChecked && this.#addObjectsCallbackHandler
            }
          >
            <ch-image
              class="icon-m"
              disabled={this.exportAllIsChecked}
              src={MENUS_NEW_OBJECT_ICON}
            ></ch-image>
            {this.#componentLocale.footer.addButton}
          </button>
        </div>
      </footer>
    );
  };

  #updateObjects = (objectsDataArray: ObjectData[]) => {
    if (objectsDataArray.length) {
      const newObjectsTreeModel = updateObjectsTreeModel(
        objectsDataArray,
        this.#objectTypesMap,
        this.#objectsSet,
        this.objectsTreeViewModel
      );
      this.objectsTreeViewModel = newObjectsTreeModel;
    }
  };

  render(): void {
    const exportButtonEnabled =
      this.atLeastOnePropertyItemIsChecked ||
      this.atLeastOneObjectItemIsChecked ||
      this.exportAllIsChecked;

    const cancelButtonEnabled = this.exportingIsInProcess;

    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <section>
          <header class="header dialog-header-with-border space-body spacing-body-inline spacing-body-block-start">
            <div class="field-group header__field-group">
              <div class="field field-inline export-file-name">
                <label class="label" htmlFor="xpz-file">
                  {this.#componentLocale.header.exportFileName}
                </label>
                <ch-edit
                  autoFocus
                  id="xpz-file"
                  class="input"
                  part="xpz-file"
                  value={this.fileName}
                  startImgSrc={FILE_ICON}
                  ref={(el: HTMLChEditElement) =>
                    (this.#fileNameEl = el as HTMLChEditElement)
                  }
                ></ch-edit>
              </div>

              <div class="header__buttons-container buttons-spacer">
                <button
                  id="export-kb-btn"
                  class="button-primary export-btn"
                  part="export-btn"
                  disabled={!exportButtonEnabled}
                  onClick={exportButtonEnabled && this.#exportCallbackHandler}
                >
                  {this.#componentLocale.header.exportButton}
                </button>
                <button
                  id="cancel-kb-export-btn"
                  class="button-secondary cancel-btn"
                  part="cancel-button"
                  disabled={!cancelButtonEnabled}
                  onClick={cancelButtonEnabled && this.#cancelCallbackHandler}
                >
                  {this.#componentLocale.header.cancelButton}
                </button>

                <button
                  aria-label={this.#componentLocale.header.optionsButton}
                  title={this.#componentLocale.header.optionsButton}
                  class="button-tertiary button-icon-only options-btn"
                  part="select-kb-btn"
                  onClick={this.optionsCallback}
                >
                  <ch-image class="icon-m" src={SETTINGS_ICON}></ch-image>
                </button>
              </div>

              <div class="header__checkboxes field-group">
                <ch-checkbox
                  class="checkbox add-kb-description"
                  part="add-kb-description"
                  caption={
                    this.#componentLocale.main.addKnowledgeBaseDescription
                  }
                  checkedValue={CHECKBOX_CHECKED_VALUE}
                  onInput={this.#addKBPropertiesCallbackHandler}
                  ref={(el: HTMLChCheckboxElement) =>
                    (this.#addKbDescriptionEl = el as HTMLChCheckboxElement)
                  }
                ></ch-checkbox>

                <ch-checkbox
                  class="checkbox"
                  part="export-all-checkbox"
                  checkedValue={CHECKBOX_CHECKED_VALUE}
                  caption={this.#componentLocale.main.exportAll}
                  onInput={this.#exportAllChangedHandler}
                  ref={(el: HTMLChCheckboxElement) =>
                    (this.#exportAllEl = el as HTMLChCheckboxElement)
                  }
                ></ch-checkbox>
              </div>
            </div>
          </header>

          <div class="main spacing-body-inline">
            {this.#evaluateContentToDisplay()}
            {this.loader && this.exportingIsInProcess && (
              <gx-ide-loader
                cancelLabel={this.#componentLocale.loader.cancelLabel}
                loaderTitle={this.#componentLocale.loader.title}
                cancelCallback={this.#cancelCallbackHandler}
                show
                class="loader"
              ></gx-ide-loader>
            )}
          </div>

          {this.#renderFooter()}
        </section>
      </Host>
    );
  }
}
